import { Helmet } from 'react-helmet-async';
import { SITE_NAME, SITE_URL, SITE_DEFAULT_DESCRIPTION, SITE_OG_IMAGE } from '../../constants';

export interface SeoProps {
    /** Page title. Composed as "{title} — Dragan Strukelj" (or just the site name on the home page). */
    title: string;
    /** Meta / OG / Twitter description. Falls back to the site default. */
    description?: string;
    /** OG/Twitter share image — absolute URL or site-relative path (made absolute here). */
    image?: string;
    /** Site-relative path for the canonical + og:url, e.g. "/paintings/featured". */
    canonicalPath?: string;
    /** Structured data (e.g. a schema.org VisualArtwork) emitted as JSON-LD. */
    jsonLd?: object;
    /** Keep this route out of the index (e.g. 404). */
    noindex?: boolean;
}

/** Resolve a possibly-relative URL against SITE_URL; pass through absolute URLs. */
const toAbsolute = (url?: string): string | undefined => {
    if (!url) return undefined;
    if (/^https?:\/\//i.test(url)) return url;
    return `${SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

/**
 * Per-route document head: title, description, canonical, Open Graph, Twitter card,
 * and optional JSON-LD. Requires a <HelmetProvider> ancestor (added in App.tsx).
 * Pages own their single <h1>; this component owns everything in <head>.
 */
export const Seo = ({ title, description, image, canonicalPath, jsonLd, noindex }: SeoProps) => {
    const fullTitle = title === SITE_NAME ? title : `${title} — ${SITE_NAME}`;
    const desc = description ?? SITE_DEFAULT_DESCRIPTION;
    const canonical = toAbsolute(canonicalPath);
    const ogImage = toAbsolute(image ?? SITE_OG_IMAGE);

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={desc} />
            {noindex && <meta name="robots" content="noindex, nofollow" />}
            {canonical && <link rel="canonical" href={canonical} />}

            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:type" content="website" />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={desc} />
            {canonical && <meta property="og:url" content={canonical} />}
            {ogImage && <meta property="og:image" content={ogImage} />}

            <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={desc} />
            {ogImage && <meta name="twitter:image" content={ogImage} />}

            {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
        </Helmet>
    );
};
